/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.userBusinessGiftCards

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.userBusinessGiftCards.internal.UserBusinessGiftCardsUseCase
import features.userBusinessGiftCards.internal.UserBusinessGiftCardsUseCase.Params
import features.userBusinessGiftCards.models.UserBusinessGiftCardsModel


typealias UserBusinessGiftCardsState = CFlow<UserBusinessGiftCardsUseCaseResult?>

typealias UserBusinessGiftCardsUseCaseResult = Result<UserBusinessGiftCardsModel>

fun UserBusinessGiftCardsUseCaseResult.serializeUserBusinessGiftCardsApiResult(): SerializedResult<String> {
    return this.toJsonResult<UserBusinessGiftCardsModel>()
}

/**
 * Serializes [UserBusinessGiftCardsState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun UserBusinessGiftCardsState.serializeUserBusinessGiftCardsState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<UserBusinessGiftCardsModel>()
    }.asCFlow()
}

/**
 * Get a list of gift cards available for purchase by specific business [Api] class
 */
class UserBusinessGiftCardsApi : Api() {

    val userBusinessGiftCardsState: UserBusinessGiftCardsState
        get() = _userBusinessGiftCardsState.asCFlow()

    companion object {
        private val _userBusinessGiftCardsState: MutableStateFlow<UserBusinessGiftCardsUseCaseResult> = MutableStateFlow(Idle())
    }

    private val userBusinessGiftCardsUseCase = UserBusinessGiftCardsUseCase()

    /**
     * Get a list of gift cards available for purchase by specific business
     */
    suspend fun getUserBusinessGiftCards(
        overrideHeaders: Map<String, String>? = null,
        businessId: String
    ): Result<UserBusinessGiftCardsModel> {
        /* set state to Loading as soon as function is called */
        _userBusinessGiftCardsState.value = Loading()
        val result = this.userBusinessGiftCardsUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                businessId = businessId
            )
        ) { result ->
            _userBusinessGiftCardsState.value = result
            return@userBusinessGiftCardsUseCase result
        }
        return result
    }

}
